import React from "react"
import {Box, Skeleton} from "@mui/material"
import { AmchartSeeker } from "@ui-seeker/core"

import AnalyzerEvolutionHeader from "./Header"
import { evolutionChartVisibility, openEvolutionModal } from "Pages/AnalyzersV2/@signals"
import { POSITION_MAIN_COLOR } from "Pages/AnalyzersV2/@constants"
import { isRankEvolutionData } from "Pages/AnalyzersV2/@components/EvolutionModal/@utils"
import {
	buildRankData,
	findMainDataKey,
	getMaxRank,
	priceField
} from "Pages/AnalyzersV2/@components/RankEvolutionChart/@utils"


export default function AnalyzerEvolutionChart({ xTitle, isLoading, chartData, keySeries, isRating }) {
	const { tableRows } = openEvolutionModal.value
	if (isLoading) return <Box>
		{tableRows?.length >= 2 && !isRating && <Skeleton variant="rounded" height={75} sx={{ mb: .5, borderRadius: '10px' }}/>}
		{tableRows?.length >= 3 && !isRating && <Skeleton variant="rounded" height={75} sx={{ mb: .5, borderRadius: '10px' }}/>}
		{tableRows?.length >= 4 && !isRating && <Skeleton variant="rounded" height={75} sx={{ mb: .5, borderRadius: '10px' }}/>}
		{!isRating && <Skeleton variant="rounded" height={75} sx={{ mb: .5, borderRadius: '10px' }}/>}
		<Skeleton variant="rounded" height='calc(100vh - 260px)' sx={{ mb: .5, borderRadius: '10px' }}/>
	</Box>

	// The position analyzer plots market rank instead of price, so its evolution
	// reads the same way as its "Rank evolution" chart.
	const isRankChart = isRankEvolutionData(chartData)
	const dataKeys = keySeries?.map(({ dataKey }) => dataKey) || []
	const data = isRankChart ? buildRankData(chartData, dataKeys) : chartData
	const mainKey = isRankChart ? findMainDataKey(chartData, dataKeys) : null
	const maxRank = isRankChart ? getMaxRank(data, dataKeys) : null

	return <AmchartSeeker
		rootID="ChartEvolutionSelected"
		data={data}
		sx={{ height: 'calc(100vh - 260px)' }}
		series={keySeries?.map(({ dataKey, color }, index) => ({
			name: dataKey,
			yField: dataKey,
			xField: 'date',
			color: isRankChart && dataKey === mainKey ? POSITION_MAIN_COLOR : color,
			hidden: !evolutionChartVisibility.value.includes(dataKey),
			// A missing rank means no rate that day, so the line has to break.
			connect: !isRankChart,
			tension: 1,
			...isRankChart && {
				strokes: { strokeWidth: dataKey === mainKey ? 4 : 2 },
				tooltip: {
					pointerOrientation: 'horizontal',
					labelText: `{name}: rank {valueY.formatNumber("#")} · {${priceField(index)}}`,
				},
			},
		}))}
		extraHeader={<AnalyzerEvolutionHeader isRating={isRating} />}
		chartProps={{
			// Ranks of a date sit a few pixels apart: one label, the brand under the pointer, as in the
			// Rank evolution chart
			...isRankChart && { chart: { maxTooltipDistance: -1 } },
			enabledPDF: false,
			numberFormat: `#,###.00`,
			dateFormatReceived: 'yyyyMMdd',
			xAxis: {
				categoryField: 'date',
				isDateAxis: true,
				dateFormat: 'dd MMM yyyy (EEE)',
				title: xTitle,
				baseInterval: {
					timeUnit: 'day',
					count: 1
				}
			},
			yAxis: isRankChart
				? {
					title: 'Market rank (1 = cheapest)',
					min: .5,
					max: maxRank + .5,
					maxPrecision: 0,
					// 1 on top, so a better position sits higher on the chart.
					renderer: { inversed: true },
				}
				: { title: isRating ? 'Rating' : 'Prices' },
		}}
	/>
}